<script setup lang="ts">
/**
 * Gateway payment-method toggles — card, BLIK, Apple Pay, Open Banking in;
 * card (OCT) and SEPA out. Rendered at the bottom of the Overview pane, the
 * same place Dominion and Hearthforge keep theirs.
 *
 * Not to be confused with `PaymentMethodsPane`, which only decides which
 * *logos* the landing footer shows. These switches decide what a player can
 * actually pay with, through core's `/api/admin/payments/config`.
 *
 * ── Unconfigured terminals ───────────────────────────────────────────────────
 *
 * `configured: false` means the terminal's credentials are missing. The toggle
 * is greyed out and cannot be clicked — flipping it would do nothing useful —
 * and the hint says to contact ops. Which credentials are missing is an ops
 * concern and never shown here (the API does not expose it either).
 *
 * ── Payouts in Steel of War ──────────────────────────────────────────────────
 *
 * Steel of War runs with withdrawals off (`withdrawalsEnabled: false`), so no
 * payout method is ever active. The toggles still show and still save their
 * state, so turning withdrawals on later does not need a second trip here.
 *
 * ── Optimistic, with a revert ────────────────────────────────────────────────
 *
 * A toggle flips immediately. If the PATCH fails — notably 409
 * `METHOD_NOT_CONFIGURED`, when credentials vanished since the page loaded —
 * it flips back and says why.
 */
import { ref } from 'vue';

import { ApiError } from '@/net/api';

import { adminApi, type PaymentConfigMethod, type PaymentsConfig } from './admin-api';
import { usePane } from './pane';

type Kind = 'deposit' | 'payout';

const NOT_CONFIGURED_HINT = 'Terminal credentials missing — contact ops';

const config = usePane(() => adminApi.paymentsConfig());
const saving = ref<string | null>(null);
const message = ref<string | null>(null);

function inactiveReason(cfg: PaymentsConfig, kind: Kind, m: PaymentConfigMethod): string | null {
  if (m.active || !m.configured) return null;
  if (kind === 'payout' && !cfg.withdrawalsEnabled) {
    return 'Withdrawals are disabled for this game';
  }
  if (!m.enabled) return null;
  if (m.inactiveReason) return m.inactiveReason;
  if (!cfg.adopted) return 'Core payments are not enabled for this game';
  if (m.currencies.length === 0) return 'No currency available';
  return 'Inactive';
}

async function toggle(kind: Kind, m: PaymentConfigMethod): Promise<void> {
  if (!m.configured || saving.value) return;
  const next = !m.enabled;
  const key = `${kind}.${m.id}`;
  saving.value = key;
  message.value = null;
  m.enabled = next;
  try {
    config.data.value = await adminApi.updatePaymentsConfig({ [kind]: { [m.id]: { enabled: next } } });
  } catch (caught) {
    m.enabled = !next;
    if (caught instanceof ApiError && caught.status === 409) {
      m.configured = false;
      message.value = `${m.label} cannot be enabled — terminal credentials missing. Contact ops.`;
    } else if (caught instanceof ApiError) {
      message.value = caught.message || `Could not save ${m.label} (${caught.status}).`;
    } else {
      message.value = 'Could not reach the server.';
    }
  } finally {
    saving.value = null;
  }
}

async function toggleWithdrawals(): Promise<void> {
  if (!config.data.value || saving.value) return;
  const next = !config.data.value.withdrawalsEnabled;
  saving.value = 'withdrawals';
  message.value = null;
  config.data.value.withdrawalsEnabled = next;
  try {
    config.data.value = await adminApi.updatePaymentsConfig({ withdrawalsEnabled: next });
  } catch (caught) {
    config.data.value.withdrawalsEnabled = !next;
    if (caught instanceof ApiError) {
      message.value = caught.message || `Could not save withdrawals setting (${caught.status}).`;
    } else {
      message.value = 'Could not reach the server.';
    }
  } finally {
    saving.value = null;
  }
}

const GROUPS: ReadonlyArray<{ kind: Kind; title: string }> = [
  { kind: 'deposit', title: 'Deposits' },
  { kind: 'payout', title: 'Payouts' },
];
</script>

<template>
  <section data-test="admin-payment-config">
    <h2>Payment methods</h2>
    <p v-if="config.error.value" class="bad">{{ config.error.value }}</p>
    <p v-if="message" class="bad" data-test="admin-payment-config-message">{{ message }}</p>
    <p v-if="config.loading.value && !config.data.value" class="busy">Loading…</p>

    <template v-if="config.data.value">
      <div v-for="group in GROUPS" :key="group.kind" class="pay-group">
        <h3>{{ group.title }}</h3>
        <div v-if="group.kind === 'payout'" class="withdrawals-toggle" data-test="admin-withdrawals-toggle">
          <label class="pay-toggle">
            <input
              type="checkbox"
              role="switch"
              :checked="config.data.value.withdrawalsEnabled"
              :disabled="saving !== null"
              aria-label="Enable withdrawals"
              data-test="admin-pay-toggle-withdrawals"
              @change="toggleWithdrawals()"
            />
            <span>{{ config.data.value.withdrawalsEnabled ? 'On' : 'Off' }}</span>
          </label>
          <span class="withdrawals-label">Enable withdrawals</span>
          <p v-if="!config.data.value.withdrawalsEnabled" class="note" data-test="admin-payouts-off">
            Withdrawals are disabled for this game, so no payout method is active.
          </p>
        </div>
        <ul class="pay-list">
          <li
            v-for="m in config.data.value[group.kind]"
            :key="m.id"
            class="pay-row"
            :class="{ unconfigured: !m.configured }"
            :data-test="`admin-pay-${group.kind}-${m.id}`"
          >
            <div class="pay-text">
              <span class="pay-name">{{ m.label }}</span>
              <span class="pay-badges">
                <span v-if="!m.configured" class="chip bad" data-test="not-configured">
                  Not configured
                </span>
                <span v-else-if="m.active" class="chip ok">Active</span>
                <span v-else class="chip warn">Inactive</span>
              </span>
              <span
                v-if="inactiveReason(config.data.value, group.kind, m)"
                class="pay-reason"
                data-test="inactive-reason"
              >
                {{ inactiveReason(config.data.value, group.kind, m) }}
              </span>
            </div>
            <label
              class="pay-toggle"
              :title="m.configured ? undefined : NOT_CONFIGURED_HINT"
            >
              <input
                type="checkbox"
                role="switch"
                :checked="m.enabled"
                :disabled="!m.configured || saving !== null"
                :aria-label="`${m.label} ${group.title.toLowerCase()}`"
                :data-test="`admin-pay-toggle-${group.kind}-${m.id}`"
                @change="toggle(group.kind, m)"
              />
              <span>{{ m.enabled ? 'On' : 'Off' }}</span>
            </label>
          </li>
        </ul>
      </div>
    </template>
  </section>
</template>

<style scoped>
.pay-group + .pay-group {
  margin-top: 0.9rem;
}

.pay-group h3 {
  margin: 0 0 0.4rem;
  color: var(--steel-400);
  font-size: 0.75rem;
  font-weight: 600;
}

.pay-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.pay-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.2rem;
  background: var(--steel-800);
}

.pay-row.unconfigured {
  opacity: 0.55;
}

.pay-text {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.4rem;
  min-width: 0;
}

.pay-name {
  color: var(--steel-200);
  font-size: 0.8rem;
}

.pay-reason {
  flex-basis: 100%;
  color: var(--steel-400);
  font-size: 0.7rem;
}

.pay-toggle {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
  color: var(--steel-400);
  font-family: var(--font-mono);
  font-size: 0.6rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
}

.pay-toggle input {
  width: 1rem;
  height: 1rem;
  min-height: 0;
  cursor: pointer;
}

.pay-row.unconfigured .pay-toggle,
.pay-row.unconfigured .pay-toggle input {
  cursor: not-allowed;
}

.withdrawals-toggle {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem 0.6rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.2rem;
  background: var(--steel-800);
}

.withdrawals-label {
  color: var(--steel-200);
  font-size: 0.8rem;
}

.withdrawals-toggle .note {
  flex-basis: 100%;
  margin: 0.2rem 0 0;
}
</style>
